import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function ShippingSection({ order, onShipmentCreated, onWaybillUpdated }) {
  const { isPrivileged } = useAuth();
  const [mode, setMode] = useState('idle'); // 'idle' | 'manual' | 'delhivery'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ waybill_number: '', waybill_date: '', awb_number: '' });
  const [delhiveryFields, setDelhiveryFields] = useState({ pincode: '', weight: '0.5' });

  useEffect(() => {
    setForm({
      waybill_number: order.waybill_number || '',
      waybill_date:   order.waybill_date ? order.waybill_date.split('T')[0] : '',
      awb_number:     order.awb_number || '',
    });
  }, [order.waybill_number, order.waybill_date, order.awb_number]);

  const hasShipping = order.waybill_number || order.awb_number;

  const handleManualSave = async () => {
    if (!form.waybill_number.trim() && !form.awb_number.trim()) {
      setError('Enter at least a Waybill Number or an AWB Number.');
      return;
    }
    setLoading(true); setError('');
    try {
      const payload = {
        waybill_number: form.waybill_number.trim() || undefined,
        waybill_date:   form.waybill_date || undefined,
        awb_number:     form.awb_number.trim() || undefined,
      };
      const { data } = await api.patch(`/orders/${order.id}/waybill`, payload);
      onWaybillUpdated && onWaybillUpdated(data.data);
      setMode('idle');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save shipping details.');
    } finally { setLoading(false); }
  };

  const handleDelhivery = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await api.post(`/orders/${order.id}/shipment`, delhiveryFields);
      onShipmentCreated && onShipmentCreated(data);
      setMode('idle');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create shipment.');
    } finally { setLoading(false); }
  };

  // ── Shipping info already set ────────────────────────────────────────────
  if (hasShipping && mode === 'idle') {
    return (
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#15803d', marginBottom: 6 }}>
          Shipping Details
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem', color: '#166534' }}>
          {order.waybill_number && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, minWidth: 80 }}>Waybill No:</span>
              <span><strong>{order.waybill_number}</strong></span>
              {order.waybill_date && (
                <span style={{ background: '#dcfce7', padding: '1px 8px', borderRadius: 10 }}>
                  {new Date(order.waybill_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              )}
              {order.shipping_status && (
                <span style={{ background: '#dcfce7', padding: '1px 8px', borderRadius: 10 }}>
                  {order.shipping_status}
                </span>
              )}
              {order.tracking_url && (
                <a href={order.tracking_url} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#0891b2', fontWeight: 600, textDecoration: 'none' }}>
                  Track &rarr;
                </a>
              )}
            </div>
          )}
          {order.awb_number && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, minWidth: 80 }}>AWB No:</span>
              <span><strong>{order.awb_number}</strong></span>
            </div>
          )}
        </div>
        <button
          className="btn btn-sm btn-outline-secondary"
          style={{ marginTop: 8, fontSize: '0.75rem' }}
          onClick={() => setMode('manual')}
        >
          Edit Shipping Details
        </button>
      </div>
    );
  }

  // ── Choose mode (idle, no shipping yet) ─────────────────────────────────
  if (mode === 'idle') {
    return (
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#475569', marginBottom: 8 }}>Shipping Details</div>
        <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic', marginBottom: 8 }}>Not yet assigned</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm btn-outline-primary" style={{ fontSize: '0.8rem' }} onClick={() => setMode('manual')}>
            Enter Manually
          </button>
          {isPrivileged && (order.order_type === 'purchase_order' || order.order_type === 'invoice') && (
            <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.8rem' }} onClick={() => setMode('delhivery')}>
              Generate via Delhivery
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Manual entry form ────────────────────────────────────────────────────
  if (mode === 'manual') {
    return (
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#475569', marginBottom: 10 }}>
          {hasShipping ? 'Edit Shipping Details' : 'Enter Shipping Details'}
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Waybill Number</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              className="form-control"
              placeholder="Waybill Number"
              value={form.waybill_number}
              onChange={e => setForm(p => ({ ...p, waybill_number: e.target.value }))}
              style={{ flex: 1, minWidth: 160, fontSize: '0.82rem' }}
            />
            <input
              className="form-control"
              type="date"
              value={form.waybill_date}
              onChange={e => setForm(p => ({ ...p, waybill_date: e.target.value }))}
              style={{ width: 150, fontSize: '0.82rem' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: 4 }}>AWB Number (Air Waybill)</div>
          <input
            className="form-control"
            placeholder="AWB Number"
            value={form.awb_number}
            onChange={e => setForm(p => ({ ...p, awb_number: e.target.value }))}
            style={{ fontSize: '0.82rem' }}
          />
        </div>

        {error && <div style={{ fontSize: '0.78rem', color: '#dc2626', marginBottom: 6 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm btn-primary" onClick={handleManualSave} disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setMode('idle'); setError(''); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Delhivery form (privileged only) ─────────────────────────────────────
  if (mode === 'delhivery') {
    return (
      <div style={{ marginTop: 12, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#475569', marginBottom: 8 }}>Create Shipment (Delhivery)</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input className="form-control" placeholder="Pincode" value={delhiveryFields.pincode}
            onChange={e => setDelhiveryFields(p => ({ ...p, pincode: e.target.value }))}
            style={{ flex: 1, fontSize: '0.82rem' }} />
          <input className="form-control" placeholder="Weight (kg)" type="number" step="0.1" value={delhiveryFields.weight}
            onChange={e => setDelhiveryFields(p => ({ ...p, weight: e.target.value }))}
            style={{ width: 110, fontSize: '0.82rem' }} />
        </div>
        {error && <div style={{ fontSize: '0.78rem', color: '#dc2626', marginBottom: 6 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm btn-primary" onClick={handleDelhivery} disabled={loading || !delhiveryFields.pincode}>
            {loading ? 'Creating…' : 'Book Shipment'}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setMode('idle'); setError(''); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return null;
}
